import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";

/**
 * useLivePrices — polls /api/live-prices every 60s for the given symbols
 * and returns a map of { SYMBOL_NO_NS: { last_price, prev_close, pct_change, change, volume } }.
 *
 * Symbols not present in the upstream Upstox snapshot are silently omitted —
 * callers should fall back to whatever EOD price they already have.
 *
 * Symbols are normalised: "RELIANCE.NS" → "RELIANCE" for both the request
 * and the response keys, so consumers can match on either form.
 */
export default function useLivePrices(symbols, intervalMs = 60000) {
  const [prices, setPrices] = useState({});
  const [ts, setTs] = useState(null);
  const symKey = (symbols || []).join(",");
  const stop = useRef(false);

  useEffect(() => {
    stop.current = false;
    if (!symbols || symbols.length === 0) {
      setPrices({});
      return undefined;
    }
    const normalised = Array.from(new Set(
      symbols.map((s) => (s || "").toString().toUpperCase()).filter(Boolean)
    ));
    if (normalised.length === 0) return undefined;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/live-prices", {
          params: { symbols: normalised.join(",") },
        });
        if (stop.current) return;
        setPrices(data?.prices || {});
        setTs(data?.ts || null);
      } catch {
        /* silent — keep last good snapshot */
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, intervalMs);
    return () => {
      stop.current = true;
      clearInterval(id);
    };
    // symKey collapses the symbol array into a stable dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symKey, intervalMs]);

  return { prices, ts };
}

/** Helper — strip ".NS" suffix to align with the cache key shape. */
export function bareSymbol(symbol) {
  if (!symbol) return "";
  const s = symbol.toString().toUpperCase();
  return s.endsWith(".NS") ? s.slice(0, -3) : s;
}
